import { Box, Flex, Text } from "@chakra-ui/react";
import { useCallback, useState } from "react";
import { HeartIcon } from "../../components/icons/HeartIcon";
import { HeartThickIcon } from "../../components/icons/HeartThickIcon";
import { ShareIcon } from "../../components/icons/ShareIcon";

interface FeedCardActionsProps {
  // Whether the parent slot is the active one — drives tabIndex so peek
  // slots don't grab focus.
  isActive: boolean;
  // Optional starting liked state. Useful for demoing a "you've already
  // liked this" treatment on some cards.
  initialLiked?: boolean;
  // If supplied, shown next to the heart and ticked up by 1 when liked.
  // Use for cards that surface a vote/like count (e.g. top_comment).
  likeCount?: number;
  // Layout density. "row" = inline pills (good for compact cards); "stack"
  // = vertical icon column (good for feature cards floating in a corner).
  layout?: "row" | "stack";
}

/**
 * Demo-only like + share affordance — no backend wiring. Heart toggles
 * filled/empty locally; share is a no-op stub. All click handlers
 * stopPropagation so they don't trigger the card's outer click target.
 */
export function FeedCardActions({
  isActive,
  initialLiked = false,
  likeCount,
  layout = "row",
}: FeedCardActionsProps) {
  const [liked, setLiked] = useState(initialLiked);

  const onLike = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setLiked((v) => !v);
  }, []);

  const onShare = useCallback((e: React.MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    // Demo stub.
  }, []);

  const Container = layout === "stack" ? StackContainer : RowContainer;

  const displayCount =
    likeCount === undefined
      ? undefined
      : Math.max(0, likeCount + (liked ? 1 : 0) - (initialLiked ? 1 : 0));

  return (
    <Container>
      <ActionButton
        isActive={isActive}
        onClick={onLike}
        ariaLabel={liked ? "Unlike" : "Like"}
        active={liked}
      >
        {liked ? (
          <HeartThickIcon boxSize="16px" color="pink.300" />
        ) : (
          <HeartIcon boxSize="16px" color="white" />
        )}
        {displayCount !== undefined ? (
          <Text fontSize="xs" fontWeight="bold" lineHeight="1">
            {displayCount}
          </Text>
        ) : null}
      </ActionButton>
      <ActionButton
        isActive={isActive}
        onClick={onShare}
        ariaLabel="Share"
      >
        <ShareIcon boxSize="14px" color="white" />
      </ActionButton>
    </Container>
  );
}

function RowContainer({ children }: { children: React.ReactNode }) {
  return (
    <Flex gap={2} align="center">
      {children}
    </Flex>
  );
}

function StackContainer({ children }: { children: React.ReactNode }) {
  return (
    <Flex direction="column" gap={2} align="center">
      {children}
    </Flex>
  );
}

interface ActionButtonProps {
  isActive: boolean;
  onClick: (e: React.MouseEvent) => void;
  ariaLabel: string;
  active?: boolean;
  children: React.ReactNode;
}

function ActionButton({
  isActive,
  onClick,
  ariaLabel,
  active,
  children,
}: ActionButtonProps) {
  return (
    <Box
      as="button"
      type="button"
      aria-label={ariaLabel}
      tabIndex={isActive ? 0 : -1}
      onClick={onClick}
      display="inline-flex"
      alignItems="center"
      gap={1.5}
      px={2.5}
      py={1.5}
      borderRadius="full"
      bg={active ? "rgba(244, 114, 182, 0.18)" : "rgba(0,0,0,0.45)"}
      border="1px solid"
      borderColor={active ? "rgba(244,114,182,0.4)" : "rgba(255,255,255,0.12)"}
      backdropFilter="blur(6px)"
      color="white"
      cursor="pointer"
      transition="background 120ms ease, border-color 120ms ease"
      _hover={{
        bg: active ? "rgba(244, 114, 182, 0.28)" : "rgba(0,0,0,0.6)",
        borderColor: active ? "rgba(244,114,182,0.6)" : "rgba(255,255,255,0.24)",
      }}
    >
      {children}
    </Box>
  );
}
